import { ComponentType } from 'react';
import { MenuItem, ListItemText } from '@mui/material';
import i18n from '@/i18n';
import { useHasPermission, usePermissions } from '@/components/PermissionGate';
import { CopyPlanScenarioDialog } from '@/finance/CopyPlanScenarioDialog';
import { CopyPlanVersionDialog } from '@/finance/CopyPlanVersionDialog';
import { ExportPlanVersionDialog } from '@/finance/ExportPlanVersionDialog';
import { GenerateForOrgTreeDialog } from '@/finance/GenerateForOrgTreeDialog';
import { PlanLineExplainDialog } from '@/finance/PlanLineExplainDialog';
import { PlanVersionEntryAccessDialog } from '@/finance/PlanVersionEntryAccessDialog';

/**
 * Bespoke (non-DSL) actions appended to an entity's generated Actions menu, keyed by entity name.
 * The generated `{Entity}ActionsMenu` renders these at the bottom under a divider; each opens its own
 * dialog (hoisted above the menu so it survives the menu closing). Add an entry here to surface a
 * hand-written action without editing generated files.
 *
 * `label`/`section` hold the English fallback; `labelKey`/`sectionKey` point at translation keys.
 * getMenuActionExtras resolves them at call time — the generated menus render the returned strings
 * verbatim, and since they call it during render (already re-rendered on language change via their
 * own useTranslation), the resolved values follow the active language without touching generators.
 */

export interface MenuActionExtraDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export interface MenuActionExtra {
  id: string;
  label: string;
  /** Translation key for the menu item text; `label` is the English fallback. */
  labelKey?: string;
  /** Optional uppercase section caption shown above the group (mirrors the COMMANDS / workflow sections). */
  section?: string;
  /** Translation key for the section caption; `section` is the English fallback. */
  sectionKey?: string;
  /** Optional permission; the menu item is hidden when the user lacks it (the server still enforces). */
  permission?: string;
  Dialog: ComponentType<MenuActionExtraDialogProps>;
}

const registry: Record<string, MenuActionExtra[]> = {
  PlanHeader: [
    {
      id: 'generate-headers-org-tree',
      label: 'Generate Plan Headers',
      labelKey: 'Finance.MenuAction.GeneratePlanHeaders',
      section: 'More',
      sectionKey: 'actions.more',
      permission: 'Finance.PlanHeader.Create',
      Dialog: ({ entityId, open, onClose }) => (
        <GenerateForOrgTreeDialog open={open} sourceHeaderId={entityId} onClose={onClose} />
      ),
    },
  ],
  PlanLine: [
    {
      id: 'explain-dimension-derivation',
      label: 'Explain dimensions',
      labelKey: 'Finance.MenuAction.ExplainDimensions',
      section: 'Diagnostics',
      sectionKey: 'actions.diagnostics',
      permission: 'Finance.PlanEntry.View',
      Dialog: ({ entityId, open, onClose }) => (
        <PlanLineExplainDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
  ],
  PlanScenario: [
    {
      // Copies the scenario + first draft's header structure — next year, a what-if, or another
      // variant to start from. entityId = source scenario.
      id: 'copy-plan-scenario',
      label: 'New scenario (copy structure)',
      labelKey: 'Finance.CopyPlanScenario.Title',
      section: 'Planning',
      sectionKey: 'actions.planning',
      permission: 'Finance.PlanScenario.CopyPlanScenario',
      Dialog: ({ entityId, open, onClose }) => (
        <CopyPlanScenarioDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
  ],
  PlanVersion: [
    {
      id: 'explain-entry-access',
      label: 'Explain entry access',
      labelKey: 'Finance.MenuAction.ExplainEntryAccess',
      section: 'Diagnostics',
      sectionKey: 'actions.diagnostics',
      permission: 'Finance.PlanVersion.Read',
      Dialog: ({ entityId, open, onClose }) => (
        <PlanVersionEntryAccessDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
    {
      // The skipStateMachine copy command never appears in the generated Actions menu (and needs
      // input anyway) — surfaced here per the GenerateForOrgTree pattern. entityId = source version.
      id: 'copy-plan-version',
      label: 'New version (copy structure)',
      labelKey: 'Finance.CopyPlanVersion.Title',
      section: 'Planning',
      sectionKey: 'actions.planning',
      permission: 'Finance.PlanVersion.CopyPlanVersion',
      Dialog: ({ entityId, open, onClose }) => (
        <CopyPlanVersionDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
    {
      // Exporting is neither planning nor a diagnostic, so it sits last, in a section of its own,
      // rather than being read past on the way to something else. It takes a copy of data out of
      // the system without changing anything, which is why Read guards it — the same permission
      // the generated list export runs under.
      id: 'export-plan-version',
      label: 'Export version',
      labelKey: 'Finance.ExportPlanVersion.Title',
      section: 'Export',
      sectionKey: 'actions.export',
      permission: 'Finance.PlanVersion.Read',
      Dialog: ({ entityId, open, onClose }) => (
        <ExportPlanVersionDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
  ],
};

export function getMenuActionExtras(entity: string): MenuActionExtra[] {
  return (registry[entity] ?? []).map((extra) => ({
    ...extra,
    label: extra.labelKey ? i18n.t(extra.labelKey, { defaultValue: extra.label }) : extra.label,
    section:
      extra.section && extra.sectionKey
        ? i18n.t(extra.sectionKey, { defaultValue: extra.section })
        : extra.section,
  }));
}

/** One section's worth of bespoke actions, in registration order. */
export interface MenuActionExtraGroup {
  section?: string;
  items: MenuActionExtra[];
}

/**
 * The extras grouped by their section caption, preserving registration order.
 *
 * The generated menus used to render only the FIRST extra's caption over all of them, so an entity
 * with extras in two sections showed every one of them under the first section's heading — a
 * diagnostic action filed under "Planning". Grouping here keeps that fix in one place rather than
 * in every generated menu.
 */
export function groupMenuActionExtras(extras: MenuActionExtra[]): MenuActionExtraGroup[] {
  const groups: MenuActionExtraGroup[] = [];

  for (const extra of extras) {
    const current = groups[groups.length - 1];
    if (current && current.section === extra.section) {
      current.items.push(extra);
    } else {
      groups.push({ section: extra.section, items: [extra] });
    }
  }

  return groups;
}

/**
 * The extras the current user may actually see.
 *
 * The generated menus gate their section caption and divider on this rather than on the
 * registry: `MenuActionExtraItem` hides an item the user has no permission for, so gating
 * on the registered count left the caption (e.g. "MORE") and its divider stranded above
 * nothing. Also feeds `hasActions`, so the Actions button itself does not appear for the
 * sake of a section the user cannot see.
 */
export function useVisibleMenuActionExtras(entity: string): MenuActionExtra[] {
  const permissions = usePermissions();
  return getMenuActionExtras(entity).filter(
    (extra) => !extra.permission || permissions.includes(extra.permission),
  );
}

/** One bespoke menu item, hidden when the user lacks its permission. */
export function MenuActionExtraItem({ extra, onActivate }: { extra: MenuActionExtra; onActivate: () => void }) {
  const allowed = useHasPermission(extra.permission ?? '');
  if (extra.permission && !allowed) return null;
  return (
    <MenuItem onClick={onActivate}>
      <ListItemText>{extra.label}</ListItemText>
    </MenuItem>
  );
}
